import React from 'react';
import { Typography, Button, Link, Grid, Box } from '@mui/material';
import { useLogout } from 'app/hooks/useLogout';
import { useTranslation } from 'react-i18next';
import { I18nNamespace } from '@/i18n/types/i18nNamespace.ts';
import { createTranslationField } from 'app/utils/i18nUtils.ts';
import type { I18nCommonNs } from '@/i18n/dictionaries/interfaces.ts';
import heiInterpreterLogo from '/icons/logo-minimal-192.png?url';
import { getClientCorrelationId } from 'app/utils/diagnostics.ts';

import useStyles from './styles.ts';
import { isHeiDomain as checkIsHeiDomain } from 'app/utils/heiDomain.ts';

interface FallbackPageProps {
  text?: string;
  title?: string;
  redirectUrl?: string | null;
  showLogoutButton?: boolean;
  diagnosticId?: string;
  diagnosticTimestamp?: string;
  autoGenerateDiagnosticId?: boolean;
  /** Custom actions rendered below the text, replacing the default "Go home" / logout buttons */
  actions?: React.ReactNode;
}

const FallbackPage: React.FC<FallbackPageProps> = ({
  redirectUrl = '/',
  text = 'Page not found',
  title = 'An error has occurred',
  showLogoutButton = false,
  diagnosticId,
  diagnosticTimestamp,
  autoGenerateDiagnosticId = false,
  actions
}) => {
  const classes = useStyles();
  const [translate] = useTranslation([I18nNamespace.Common]);
  const i18nCommon = createTranslationField<I18nCommonNs>();
  const isHei = typeof window !== 'undefined' && checkIsHeiDomain(window.location.hostname);
  const isTranslatorPath = typeof window !== 'undefined' && window.location.pathname.includes('/translator');
  const logout = useLogout();
  const generatedDiagnostics = React.useMemo(() => {
    if (!autoGenerateDiagnosticId) {
      return { id: undefined, timestamp: undefined };
    }

    return {
      id: diagnosticId ?? getClientCorrelationId(),
      timestamp: diagnosticTimestamp ?? new Date().toISOString()
    };
  }, [autoGenerateDiagnosticId, diagnosticId, diagnosticTimestamp]);

  // Customize messages for HEI domain and translator path
  const customTitle = isHei && isTranslatorPath ? translate('fallbackPage.accessUnavailable') : title;

  const customText = isHei && isTranslatorPath ? translate('fallbackPage.noAccessMessage') : text;
  const effectiveDiagnosticId = autoGenerateDiagnosticId ? generatedDiagnostics.id : diagnosticId;
  const effectiveDiagnosticTimestamp = autoGenerateDiagnosticId ? generatedDiagnostics.timestamp : diagnosticTimestamp;
  const shouldShowDiagnostics = Boolean(effectiveDiagnosticId || effectiveDiagnosticTimestamp);
  const canCopyDiagnosticId = Boolean(effectiveDiagnosticId) && typeof navigator !== 'undefined';

  const handleCopyDiagnosticId = async () => {
    if (!effectiveDiagnosticId || typeof navigator === 'undefined') return;

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(effectiveDiagnosticId);
        return;
      }
    } catch {
      // fall through to legacy copy fallback
    }

    const textArea = document.createElement('textarea');
    textArea.value = effectiveDiagnosticId;
    textArea.style.position = 'fixed';
    textArea.style.opacity = '0';
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);
  };

  // Special styling for HEI domain
  const heiStyles = isHei
    ? {
        background: '#0e0b30',
        color: 'white',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '2rem'
      }
    : {};

  return (
    <Grid className={classes.container} sx={heiStyles}>
      {isHei && (
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <img
            src={heiInterpreterLogo}
            alt="Hei Interpreter"
            style={{ width: '120px', height: '120px', borderRadius: '50%', marginBottom: '1rem' }}
          />
          <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 2 }}>
            {translate('fallbackPage.heiInterpreterTitle')}
          </Typography>
        </Box>
      )}
      <Grid container direction="column" alignItems="center" spacing={2}>
        <Grid item>
          <Typography
            className={classes.title}
            variant="h4"
            sx={{
              color: isHei ? 'white' : 'inherit',
              textAlign: 'center'
            }}
          >
            {customTitle}
          </Typography>
        </Grid>
        {text && (
          <Grid item>
            <Typography
              className={classes.title}
              variant="subtitle1"
              sx={{
                color: isHei ? '#f0f0f0' : 'inherit',
                textAlign: 'center',
                maxWidth: '600px'
              }}
            >
              {customText}
            </Typography>
          </Grid>
        )}
        {shouldShowDiagnostics && (
          <Grid item>
            <Typography
              variant="caption"
              sx={{
                color: isHei ? '#cfcfcf' : 'text.secondary',
                textAlign: 'center',
                display: 'block'
              }}
            >
              {effectiveDiagnosticId && `Diagnostic ID: ${effectiveDiagnosticId}`}
              {effectiveDiagnosticId && effectiveDiagnosticTimestamp ? ' · ' : ''}
              {effectiveDiagnosticTimestamp && `Timestamp: ${effectiveDiagnosticTimestamp}`}
            </Typography>
            {effectiveDiagnosticId && (
              <Box sx={{ mt: 0.5, display: 'flex', justifyContent: 'center' }}>
                <Button
                  variant="text"
                  size="small"
                  disabled={!canCopyDiagnosticId}
                  onClick={handleCopyDiagnosticId}
                  sx={{ textTransform: 'none' }}
                >
                  Copy ID
                </Button>
              </Box>
            )}
          </Grid>
        )}
        <Grid item className={classes.buttonContainer}>
          {actions ? (
            actions
          ) : isHei ? (
            <Button
              variant="contained"
              onClick={logout}
              sx={{
                mt: 2,
                backgroundColor: '#f47b20',
                color: 'white',
                '&:hover': {
                  backgroundColor: '#d86a15'
                },
                borderRadius: '20px',
                padding: '8px 24px'
              }}
            >
              {translate('fallbackPage.signOut')}
            </Button>
          ) : showLogoutButton ? (
            <Button variant="contained" onClick={logout} sx={{ mt: 2 }}>
              {translate(i18nCommon.fallbackPage.logOut)}
            </Button>
          ) : (
            redirectUrl && (
              <Link href={redirectUrl} underline="hover">
                <Button variant="contained">Go home</Button>
              </Link>
            )
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default FallbackPage;
